import { useState } from 'react'
import { breadcrumbList } from '../domain/breadcrumb'
import Container from '../../../../components/Container'
import { useToast } from 'hooks/toast'
import Form, { Select } from '../../../../components/Form'
import { FilterContainer } from './styles'
import api from 'services/api'
import Button from 'components/Button'
import { useLoading } from 'hooks/loading'
import { DateInput as DatePicker } from '../../../../components/Form/date'
import Modal from 'components/Modal'

const typeOptions = [
  { name: 'XML', value: 'xml' },
  { name: 'DANFE', value: 'danfe' }
]

const DownloadXmlBatch = (): JSX.Element => {
  const [type, setType] = useState<string>('')
  const [startDate, setStartDate] = useState<Date>()
  const [endDate, setEndDate] = useState<Date>()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [summary, setSummary] = useState<{
    totalInvoices: number
    totalValue: number
  } | null>(null)

  const { activeLoading, disableLoading, loading } = useLoading()
  const { addToast } = useToast()

  const handleDownload = async () => {
    if (!type || !startDate || !endDate) {
      addToast({
        type: 'error',
        title: 'Preencha todos os campos',
        description: 'Informe o tipo, data inicial e data final.'
      })
      return
    }

    activeLoading()
    try {
      const urlBase =
        type === 'xml' ? 'xml/download/batch' : 'danfe/download/batch'

      const startDateFormatted = startDate.toISOString().split('T')[0]
      const endDateFormatted = endDate.toISOString().split('T')[0]

      const response = await api.get(
        `/financial/invoices/${urlBase}?start_date=${startDateFormatted}&end_date=${endDateFormatted}`,
        { responseType: 'blob' }
      )

      const contentDisposition = response.headers['content-disposition']
      const fileNameMatch = contentDisposition?.match(
        /filename\*?=(?:UTF-8''|")?([^";]+)/i
      )
      const fileName = fileNameMatch?.[1]
        ? decodeURIComponent(fileNameMatch[1].replace(/"/g, '').trim())
        : `batch-${type}-${startDateFormatted}-${endDateFormatted}.zip`
      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: 'application/zip' })
      const url = window.URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = fileName
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      window.URL.revokeObjectURL(url)
      addToast({ type: 'success', title: 'Download realizado com sucesso' })
    } catch (error: any) {
      const text = await error?.response?.data?.text()
      const payload = JSON.parse(text)
      const errorMessage = payload?.message || 'Erro ao realizar o download'
      console.log(payload)
      addToast({
        type: 'error',
        title: errorMessage
      })
    } finally {
      disableLoading()
    }
  }

  const handleSummary = async () => {
    if (!startDate || !endDate) {
      addToast({
        type: 'error',
        title: 'Preencha as datas',
        description: 'Informe a data inicial e a data final.'
      })
      return
    }

    activeLoading()
    try {
      const startDateFormatted = startDate.toISOString().split('T')[0]
      const endDateFormatted = endDate.toISOString().split('T')[0]
      const response = await api.get(
        `/financial/invoices/summary?start_date=${startDateFormatted}&end_date=${endDateFormatted}`
      )
      setSummary(response.data)
      setIsModalOpen(true)
    } catch (error: any) {
      addToast({
        type: 'error',
        title: error?.response?.data?.message || 'Erro ao buscar resumo'
      })
    } finally {
      disableLoading()
    }
  }

  return (
    <>
      <Container
        pageTitle="Download em lote (XML / DANFE)"
        breadcrumb={breadcrumbList}
        tools={[]}
      >
        <FilterContainer>
          <Form
            onSubmit={handleDownload}
            defaultValues={{}}
            className="col-md-12"
          >
            <div className="row">
              <div className="col-md-3 pe-8">
                <Select
                  label="Tipo"
                  name="type"
                  options={typeOptions}
                  onChange={e => setType(e.target.value)}
                  value={type}
                  rules={{ required: true }}
                  blank
                  controlled
                  defaultValue={''}
                />
              </div>
              <div className="col-md-3 pe-8">
                <DatePicker
                  label="Data inicial"
                  name="start_date"
                  selected={startDate}
                  onChange={e => setStartDate(e)}
                  controlled
                />
              </div>
              <div className="col-md-3">
                <DatePicker
                  label="Data final"
                  name="end_date"
                  selected={endDate}
                  onChange={e => setEndDate(e)}
                  controlled
                />
              </div>
            </div>
            <footer className="col-md-12 d-flex justify-content-end pb-5 gap-5">
              <button
                type="reset"
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  setType('')
                  setStartDate(undefined)
                  setEndDate(undefined)
                }}
              >
                LIMPAR
              </button>
              <Button
                type="button"
                className="btn btn-info btn-sm"
                disabled={loading}
                onClick={handleSummary}
              >
                RESUMO
              </Button>

              <Button
                type="submit"
                className="btn btn-primary btn-sm"
                disabled={loading}
              >
                BAIXAR
              </Button>
            </footer>
          </Form>
        </FilterContainer>
      </Container>

      {isModalOpen && summary && (
        <Modal
          pageTitle="Resumo do período"
          isOpenModal={isModalOpen}
          onClickButtonCancel={() => setIsModalOpen(false)}
          Children={
            <div className="p-4">
              <p>
                <strong>Total de notas:</strong> {summary.totalInvoices}
              </p>
              <p>
                <strong>Valor total:</strong>{' '}
                {summary.totalValue.toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL'
                })}
              </p>
            </div>
          }
        />
      )}
    </>
  )
}

export default DownloadXmlBatch
